import InfoBox from "../ui/InfoBox"
import SectionHeader from "../ui/SectionHeader"
import { FiEdit3 } from "react-icons/fi"
import { useResetListener } from "../../hooks/useResetListener"

const EPO_LANGUAGES = ["English", "French", "German"]

export type TitleTranslations = Record<string, string>

interface TitleSectionProps {
  value: string
  onChange: (value: string) => void
  proceedingsLanguage?: string
  translations?: TitleTranslations
  onTranslationsChange?: (translations: TitleTranslations) => void
  disabled?: boolean
}

export default function TitleSection({
  value,
  onChange,
  proceedingsLanguage,
  translations = {},
  onTranslationsChange,
  disabled = false
}: TitleSectionProps) {

  useResetListener(() => {
    onChange("")
    onTranslationsChange?.({})
  })

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!disabled) {
      onChange(e.target.value)
    }
  }

  // The other two EPO languages become optional translation fields,
  // dynamically labelled, matching the real EP1001 form — falls back to
  // showing all three as translations if no proceedings language has
  // been chosen yet.
  const translationLanguages = EPO_LANGUAGES.filter(
    (language) => language !== proceedingsLanguage
  )

  return (
    <div>
      <SectionHeader icon={<FiEdit3 size={20} />} title="Title of the invention">
        <InfoBox title="Requirements (Rule 41(2)(b) EPC)">
          The title must clearly and concisely state the technical
          designation of the invention. Fancy or trade names are not
          allowed.
        </InfoBox>
      </SectionHeader>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-gray-700">
          Title in {proceedingsLanguage || "the language of the proceedings"} *
        </label>
        <textarea
          value={value}
          onChange={handleChange}
          disabled={disabled}
          rows={2}
          className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400 ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
          placeholder="e.g. Method and apparatus for..."
        />
      </div>

      {translationLanguages.length > 0 && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {translationLanguages.map((language) => (
            <div key={language} className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                Translation into {language}
              </label>
              <textarea
                value={translations[language] || ""}
                onChange={(e) =>
                  !disabled &&
                  onTranslationsChange?.({
                    ...translations,
                    [language]: e.target.value
                  })
                }
                disabled={disabled}
                rows={2}
                className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400 ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
